import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import SearchProducer from '@/mixins/search-queries/search-producer'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppRadio from '@/components/AppRadio.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import FilterTextSearch from '@/components/FilterTextSearch.vue'
import PolicyDialog from '@/modules/Policy/scripts/policy-dialog'
import { getPolicies } from '@/modules/Policy/gql/policy.gql'
import { policyStatus, policyStatusClass, policyType } from '@/modules/Policy/helpers/policy'
import { caseTypeClass } from '@/modules/Case/helpers/case'
import { dateToDisplay } from '@/utils/dateTime'
import ExportExcel from '@/mixins/export-excel'
import { dispatchSetIsLoading } from '@/store/dispatches'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppTextField,
    AppRefresh,
    AppDialog,
    AppRadio,
    AppTextSearch,
    AppCheckbox,
    AppTableDate,
    AppDatePicker,
    FilterTextSearch
  }
})
export default class PoliciesList extends mixins(PolicyDialog, SearchProducer, ExportExcel) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Policies By Source'
    }
  ]

  filter = {
    sourceName: '',
    startDate: '',
    endDate: ''
  }

  policies: Policy[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    policyName: 'POLICY_NAME',
    effectiveDate: 'EFFECTIVE_DATE',
    expirationDate: 'EXPIRATION_DATE',
    number: 'POLICY_NUMBER',
    status: 'POLICY_STATUS',
    brokerFee: 'BROKER_FEE'
  }

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  tableColumn = [
    { field: 'number',
      title: 'Policy Number',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.policyNumber ? row.policyNumber : '-')
        ]
      }
      },
      sortable: true,
      resizable: true },
    { field: 'policyName',
      title: 'Policy Name',
      slots: { default: 'name_default' },
      resizable: true,
      sortable: true
    },
    {
      field: 'status',
      title: 'Status',
      slots: { default: 'status_default' },
      sortable: true,
      resizable: true
    },
    {
      field: 'type',
      title: 'Type',
      slots: { default: 'type_default' },
      sortable: false,
      resizable: true
    },
    {
      field: 'brokerFee',
      title: 'Broker Fee',
      slots: { default: 'broker_default' },
      sortable: true,
      resizable: true
    },
    { title: 'Source',
      field: 'source',
      slots: { default: 'source_name_default' },
      resizable: true
    },
    { title: 'Effective Date',
      field: 'effectiveDate',
      slots: {
        default: 'effective_default'
      },
      resizable: true,
      sortable: true
    },
    { title: 'Expiration Date',
      field: 'expirationDate',
      slots: {
        default: 'expiration_default'
      },
      sortable: true
    }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  policyStatus = policyStatus
  policyStatusClass = policyStatusClass
  policyType = policyType
  caseTypeClass = caseTypeClass

  get sourceId () {
    const source = this.candidatesProducer.find(source => {
      return source.name === this.filter.sourceName
    })

    return source ? source.id : null
  }

  get showExportBtn () {
    return this.policies.length > 0
  }

  async mounted () {
    this.onResize()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    this.routeTo('PolicyDetailDetail', {
      id: item.id
    })
  }

  routeToSourceDetail (item: any) {
    this.routeTo('SourceDetailDetail', {
      id: item.id
    })
  }

  async getPolicies () {
    const { sourceId, filter: {
      startDate, endDate
    } } = this
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableOptions.loading = true

    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicies,
        variables: {
          filter: {
            sourceId: Number(sourceId),
            effectiveStartDate: startDate ? startDate.toString() : null,
            effectiveEndDate: endDate ? endDate.toString() : null
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.policies = result.data.getPolicies.policies
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getPolicies.paging.totalCount
      return result.data.getPolicies.policies
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  refreshList () {
    this.filter = {
      sourceName: '',
      startDate: '',
      endDate: ''
    }
    this.policies = []
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getPolicies()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getPolicies()
  }

  async getAllFilteredPolicies (offset: number, limit: number) {
    const { sourceId, filter: {
      startDate, endDate
    } } = this

    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicies,
        variables: {
          filter: {
            sourceId: Number(sourceId),
            effectiveStartDate: startDate,
            effectiveEndDate: endDate
          },
          order: {
            orderBy: 'ID',
            asc: false
          },
          paging: {
            offset,
            limit
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      return result.data.getPolicies.policies
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async exportHandler () {
    let allFilteredPolicies: Policy[] = []
    dispatchSetIsLoading(true)
    const total = this.tableOptions.tablePage.total
    for (let i = 0; i < total; i = i + 100) {
      const policies = await this.getAllFilteredPolicies(i, 100)
      allFilteredPolicies = allFilteredPolicies.concat(policies)
    }

    this.tHeaders = [[
      'Policy Name',
      'Client Name',
      'Policy Number',
      'Source',
      'Sub Source',
      'Product',
      'Policy Type',
      'Broker Fee',
      'Effective Date',
      'Premium',
      'Commission',
      'Sub Source Commission'
    ]]

    this.tableDatas[0] = allFilteredPolicies.map((policy: Policy) => {
      const {
        policyName, account, policyNumber, type, brokerFee,
        effectiveDate, premium, totalProjectedCommission,
        carrierProduct, source
      } = policy
      return {
        policyName,
        account: account ? account.name : '',
        policyNumber: policyNumber || '',
        source: source.name,
        carrierProduct: carrierProduct ? carrierProduct.name : '',
        type,
        brokerFee,
        effectiveDate: effectiveDate ? dateToDisplay(effectiveDate) : '',
        premium: premium + '',
        totalProjectedCommission: totalProjectedCommission + ''
      }
    })
    this.fileName = `policies by source ${this.filter.sourceName}`
    if (this.$R.isEmpty(this.tableDatas[0])) {
      return
    }
    this.filterVals[0] = this.$R.keys(this.tableDatas[0][0])
    this.export2Excel()
    dispatchSetIsLoading(false)
  }
}
